import { CheckCircle2, Clipboard, KeyRound, User } from 'lucide-react';
import { useState, useEffect, useCallback, memo, useMemo } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { useAuth } from '@/store';

interface RequestSuccessDialogProps {
  isOpen: boolean;
  onNewRequest: () => void;
  requestId: string;
  total: number;
  itemsCount: number;
  supplierName: string;
  isEditMode?: boolean;
  handleReset?: () => void;
}

// Memoized component for user information display
const UserInfoDisplay = memo(
  ({
    name,
    avatar,
    jobTitle,
  }: {
    name: string;
    avatar: string;
    jobTitle: string;
  }) => (
    <div className="flex items-center gap-3">
      <Avatar className="h-9 w-9 border">
        <AvatarImage src={avatar} alt={name} />
        <AvatarFallback>
          <User className="h-5 w-5" />
        </AvatarFallback>
      </Avatar>
      <div>
        <div className="text-sm font-medium">{name}</div>
        <div className="text-xs text-muted-foreground">{jobTitle}</div>
      </div>
    </div>
  ),
);
UserInfoDisplay.displayName = 'UserInfoDisplay';

// Memoized component for stats display
const StatsDisplay = memo(
  ({
    itemsCount,
    total,
    formatNumber,
  }: {
    itemsCount: number;
    total: number;
    formatNumber: (num: number) => string;
  }) => (
    <div className="mb-3 grid grid-cols-2 gap-2">
      <div className="rounded-md bg-white p-2 shadow-sm dark:bg-slate-800">
        <div className="text-xs text-muted-foreground">عدد الأصناف</div>
        <div className="mt-1 text-lg font-bold">{itemsCount}</div>
      </div>
      <div className="rounded-md bg-white p-2 shadow-sm dark:bg-slate-800">
        <div className="text-xs text-muted-foreground">إجمالي الطلب</div>
        <div className="mt-1 text-lg font-bold text-green-600 dark:text-green-400">
          {formatNumber(total)}
        </div>
      </div>
    </div>
  ),
);
StatsDisplay.displayName = 'StatsDisplay';

// Main component - memoized for performance
export const RequestSuccessDialog = memo(
  ({
    isOpen,
    onNewRequest,
    requestId,
    total,
    itemsCount,
    supplierName,
    isEditMode = false,
    handleReset,
  }: RequestSuccessDialogProps) => {
    const [copied, setCopied] = useState(false);

    // Get user data from Zustand store
    const { user: userData } = useAuth();

    // Memoize the user object to prevent unnecessary re-renders
    const user = useMemo(
      () => ({
        name: userData?.empName || 'المستخدم',
        avatar: userData?.avatar
          ? `${import.meta.env.VITE_HEALTHY_EMPIRE_SERVER}/public/avatar/thumb/${userData?.avatar}`
          : '/images/no-user.jpg',
        jobTitle: userData?.job?.jobName || 'الوظيفة',
      }),
      [userData],
    );

    // Format number utility - memoized to prevent recreation
    const formatNumber = useCallback((num: number) => {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }, []);

    // Memoized copy function for better performance
    const handleCopyRequestId = useCallback(() => {
      if (navigator.clipboard && requestId) {
        navigator.clipboard
          .writeText(requestId)
          .then(() => {
            setCopied(true);
            toast({
              description: 'تم نسخ رقم الطلب إلى الحافظة',
            });

            // Reset copied state after animation
            const timer = setTimeout(() => setCopied(false), 2000);
            return () => clearTimeout(timer);
          })
          .catch(() => {
            toast({
              description: 'حدث خطأ أثناء نسخ رقم الطلب',
              variant: 'destructive',
            });
          });
      }
    }, [requestId]);

    // Add keyboard shortcut for Enter key - optimized with useCallback
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter' && isOpen) {
          event.preventDefault();
          if (isEditMode && handleReset) {
            handleReset();
          } else {
            onNewRequest();
          }
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen, onNewRequest, isEditMode, handleReset]);

    // Define handler for dialog closing
    const handleDialogChange = useCallback(() => {
      if (isEditMode && handleReset) {
        handleReset();
      } else {
        onNewRequest();
      }
    }, [handleReset, isEditMode, onNewRequest]);

    // Skip rendering if not open
    if (!isOpen) return null;

    return (
      <Dialog open={isOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="max-w-md">
          <DialogHeader className="flex flex-col items-center text-center">
            <div
              className={`mb-2 rounded-full ${isEditMode ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}
            >
              <CheckCircle2
                className={`h-12 w-12 ${isEditMode ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}
              />
            </div>
            <DialogTitle className="text-2xl">
              {isEditMode ? 'تم تعديل الطلب بنجاح' : 'تم إنشاء الطلب بنجاح'}
            </DialogTitle>
            <DialogDescription className="mt-1">
              {isEditMode
                ? `تم تعديل طلب الشراء بنجاح للمورد ${supplierName}`
                : `تم حفظ طلب شراء جديد للمورد ${supplierName}`}
            </DialogDescription>

            {/* Keyboard shortcut hint - only show in create mode */}
            {!isEditMode && (
              <div className="mt-1 flex items-center justify-center gap-1 text-xs text-muted-foreground">
                <KeyRound className="h-3 w-3" />
                <span>اضغط Enter لإنشاء طلب جديد</span>
              </div>
            )}
          </DialogHeader>

          <div className="mt-2 rounded-lg border bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
                رقم الطلب
              </div>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-8 gap-1 text-slate-600 dark:text-slate-300',
                  copied && 'text-green-600 dark:text-green-400',
                )}
                onClick={handleCopyRequestId}
              >
                {requestId}
                <Clipboard className="h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Stats Display - memoized component */}
            <StatsDisplay
              itemsCount={itemsCount}
              total={total}
              formatNumber={formatNumber}
            />

            <Separator className="my-3" />

            {/* User Info Display - memoized component */}
            <UserInfoDisplay
              name={user.name}
              avatar={user.avatar}
              jobTitle={user.jobTitle}
            />
          </div>

          <DialogFooter className="mt-3">
            {isEditMode ? (
              <Button
                onClick={handleDialogChange}
                className="w-full bg-gradient-to-b from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 dark:from-slate-700 dark:to-slate-800 dark:hover:from-slate-800 dark:hover:to-slate-900"
              >
                إغلاق
              </Button>
            ) : (
              <Button
                onClick={onNewRequest}
                className="w-full bg-gradient-to-b from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 dark:from-green-700 dark:to-green-800 dark:hover:from-green-800 dark:hover:to-green-900"
              >
                بدء طلب جديد
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);

RequestSuccessDialog.displayName = 'RequestSuccessDialog';
